import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Pre-fetch backend prices for the SAME product combo at multiple
 * alternate sizes so the Quick Quote panel can show real comparison
 * pricing across 2"×2" / 3"×3" / 4"×4" / 5"×5" tiles instead of
 * falling through to a stale legacy formula.
 *
 * iter114s.14 — fixes the customer-flagged "$178.78 actual vs $336.11
 * shown" bug where alt-size tiles displayed prices ~3× higher than
 * the real backend Excel-sourced numbers because the frontend was
 * computing them via a hard-coded `basePrice * pages * (1-discount)`
 * formula whose magnitude doesn't match the catalog.
 *
 * Design parallels `useBackendPrices` but fetches a SIZE × TIER grid
 * (default 4 sizes × 12 quantity tiers = 48 calls) up-front, in
 * parallel, with full caching keyed by (combo, tier). Once primed,
 * `lookup(width, height, qty)` is synchronous + zero-roundtrip and
 * uses log-linear interpolation between tiers for non-standard
 * quantities — same algorithm as `useBackendPrices.lookup`.
 *
 * Returns a stable `lookup` reference plus a `status` machine.
 */
const QTY_TIERS = [1, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000];

const DEFAULT_SIZES = [
  { wIn: 2, hIn: 2 },
  { wIn: 3, hIn: 3 },
  { wIn: 4, hIn: 4 },
  { wIn: 5, hIn: 5 },
];

const _logLerp = (points, qty) => {
  if (!points.length) return null;
  if (points.length === 1) return points[0][1];
  const exact = points.find((pt) => pt[0] === qty);
  if (exact) return exact[1];
  const [qmin, pmin] = points[0];
  const [qmax, pmax] = points[points.length - 1];
  if (qty <= qmin) return (pmin * qty) / qmin;
  if (qty >= qmax) {
    const [q1, p1] = points[points.length - 2];
    const [q2, p2] = points[points.length - 1];
    const slope = (Math.log(p2) - Math.log(p1)) / (Math.log(q2) - Math.log(q1));
    return Math.exp(Math.log(p2) + slope * (Math.log(qty) - Math.log(q2)));
  }
  for (let i = 0; i < points.length - 1; i += 1) {
    const [q1, p1] = points[i];
    const [q2, p2] = points[i + 1];
    if (q1 <= qty && qty <= q2) {
      const lp =
        Math.log(p1) +
        ((Math.log(p2) - Math.log(p1)) *
          (Math.log(qty) - Math.log(q1))) /
          (Math.log(q2) - Math.log(q1));
      return Math.exp(lp);
    }
  }
  return pmax;
};

export const useAltSizePrices = ({
  combo,
  tier = 'stickeryou',
  sizes = DEFAULT_SIZES,
  enabled = true,
}) => {
  const [status, setStatus] = useState('idle');
  // grid[`${w}x${h}`] = { qty: cad, qty: cad, ... }
  const [grid, setGrid] = useState({});
  const cacheRef = useRef(new Map());
  const abortRef = useRef(null);

  const cacheKey = combo
    ? `${combo.category}|${combo.format}|${combo.finish}|${tier}|${sizes
        .map((s) => `${s.wIn}x${s.hIn}`)
        .join(',')}`
    : null;

  useEffect(() => {
    if (!enabled || !combo) {
      setStatus('idle');
      setGrid({});
      return;
    }
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setGrid(cached);
      setStatus('ready');
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus('loading');

    // sizes × QTY_TIERS = 4 × 12 = 48 parallel calls. Each is tiny
    // (POST /api/pricing/calculate). With browser HTTP/2 pipelining
    // this completes in 200-400 ms on first hit, free thereafter.
    const requests = [];
    sizes.forEach((s) => {
      QTY_TIERS.forEach((q) => {
        requests.push(
          axios
            .post(
              `${API_URL}/api/pricing/calculate`,
              {
                category: combo.category,
                format: combo.format,
                finish: combo.finish,
                width: s.wIn,
                height: s.hIn,
                quantity: q,
                tier,
              },
              { signal: controller.signal },
            )
            .then((r) => ({
              key: `${s.wIn}x${s.hIn}`,
              q,
              cad: r.data?.cad ?? null,
            }))
            .catch((err) => {
              // 404 / 422 → no price for that combo+size; we just
              // skip it. The lookup() call will return null for
              // missing entries and the caller falls back to the
              // active subtotal display gracefully.
              if (
                err?.response?.status === 404 ||
                err?.response?.status === 422
              ) {
                return { key: `${s.wIn}x${s.hIn}`, q, cad: null };
              }
              throw err;
            }),
        );
      });
    });

    Promise.all(requests)
      .then((results) => {
        if (controller.signal.aborted) return;
        const next = {};
        results.forEach(({ key, q, cad }) => {
          if (cad !== null && cad > 0) {
            next[key] = next[key] || {};
            next[key][q] = cad;
          }
        });
        cacheRef.current.set(cacheKey, next);
        setGrid(next);
        setStatus('ready');
      })
      .catch((err) => {
        if (axios.isCancel?.(err) || err?.name === 'CanceledError') return;
        setStatus('error');
      });

    return () => controller.abort();
  }, [enabled, combo, cacheKey, tier, sizes]);

  /**
   * Synchronous CAD price lookup for an arbitrary (size, qty). Returns
   * `null` when the combo has no catalog data for that size, when the
   * grid hasn't loaded yet, or when the fetch errored. Caller decides
   * how to render fallback (e.g. "—" or "Estimate").
   */
  const lookup = useCallback(
    (wIn, hIn, qty) => {
      const k = `${wIn}x${hIn}`;
      const tiers = grid[k];
      if (!tiers) return null;
      const points = Object.entries(tiers)
        .map(([q, p]) => [Number(q), p])
        .sort((a, b) => a[0] - b[0]);
      return _logLerp(points, qty);
    },
    [grid],
  );

  return { lookup, status, grid };
};

export default useAltSizePrices;
